/**
 * CooldownStore — Singleton that tracks ability cooldowns and GCD.
 *
 * Decoupled from the player schema object so cooldown state survives
 * Colyseus state patches (which replace the React player object).
 * PlayerManager writes here on ability execution; ActionBarSlot reads.
 */

import type { AbilityChargeConfig, AbilityChargeState } from '../../../shared/combat/abilityCharges';
import type { AbilityChargeSnapshot } from '../../../shared/combat/AbilityChargeWire';
import { AbilityChargePrediction } from './AbilityChargePrediction';

/** An open recast chain (Harebound's 2nd/3rd bound): presses left until `until`. */
export interface RecastWindow {
  until: number;
  castsLeft: number;
  windowMs: number;
}

class CooldownStoreImpl {
  private cooldowns: Record<string, number> = {};
  private lastGCDTime = 0;
  private recastWindows: Record<string, RecastWindow> = {};
  private chargePrediction = new AbilityChargePrediction();
  private onClearHooks: Array<() => void> = [];

  registerClearHook(hook: () => void): void {
    this.onClearHooks.push(hook);
  }

  resetPredictions(): void { this.chargePrediction.resetPredictions(); }

  beginChargeOwner(sessionId: string | null, characterId = ''): void {
    this.chargePrediction.beginOwner(sessionId, characterId);
  }

  applyChargeSnapshot(snapshot: AbilityChargeSnapshot): boolean {
    return this.chargePrediction.applySnapshot(snapshot);
  }

  setCooldown(abilityId: string, expiresAt: number): void {
    this.cooldowns[abilityId] = expiresAt;
  }

  getCooldown(abilityId: string): number {
    return this.cooldowns[abilityId] || 0;
  }

  setGCDTime(time: number): void {
    this.lastGCDTime = time;
  }

  getGCDTime(): number {
    return this.lastGCDTime;
  }

  /** Record (or, with null, close) the ability's open recast window. */
  setRecastWindow(abilityId: string, window: RecastWindow | null): void {
    if (window) this.recastWindows[abilityId] = window;
    else delete this.recastWindows[abilityId];
  }

  getRecastWindow(abilityId: string): RecastWindow | undefined {
    return this.recastWindows[abilityId];
  }

  // ── Ability Charges (Lanternskip capacity 2 / serial 5s recharge) ─────

  initCharges(abilityId: string, config?: AbilityChargeConfig): AbilityChargeState {
    return this.chargePrediction.init(abilityId, config);
  }

  getCharges(abilityId: string, now = Date.now()): AbilityChargeState | undefined {
    return this.chargePrediction.get(abilityId, now);
  }

  canSpendCharge(abilityId: string, now = Date.now()) {
    return this.chargePrediction.canSpend(abilityId, now);
  }

  consumeCharge(abilityId: string, now = Date.now(), clientCastSeq?: number): boolean {
    return this.chargePrediction.consume(abilityId, now, clientCastSeq);
  }

  refundCharge(abilityId: string, _now = Date.now(), clientCastSeq?: number): boolean {
    return this.chargePrediction.refund(abilityId, clientCastSeq);
  }

  syncCharges(abilityId: string, currentCharges: number, rechargeStartedAt: number | null = null, lastCastAt: number | null = null): void {
    this.chargePrediction.sync(abilityId, { currentCharges, rechargeStartedAt, lastCastAt });
  }

  hydrateFullCharges(chargesMap: Record<string, unknown> = {}): void {
    this.chargePrediction.hydrateLegacy(chargesMap);
  }

  clear(): void {
    this.cooldowns = {};
    this.lastGCDTime = 0;
    this.recastWindows = {};
    this.chargePrediction.clear();
    for (const hook of this.onClearHooks) hook();
  }
}

export const CooldownStore = new CooldownStoreImpl();

if (typeof window !== 'undefined') {
  (window as any).CooldownStore = CooldownStore;
}
